import { PRIOR_AUTH_PREP_ITEMS, PriorAuthRequirement } from '@bariendo/lib/constants';
import { isPreQuestionnaireSet, PrepItem } from '@bariendo/lib/payment-status';
import { Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import { CSSProperties, ReactElement } from 'react';
import { QUEUE_DONE_TICK_COLOR, QUEUE_EMPTY_COLOR } from '../../components/QueueCells';

/** RD rather than Dietitian, to fit; Questionnaire written out (`intake-hub.mdx`, BAR-3128). */
const LABELS: Record<PriorAuthRequirement, string> = {
  [PriorAuthRequirement.QUESTIONNAIRE]: 'Questionnaire',
  [PriorAuthRequirement.RD_CLEARANCE]: 'RD',
  [PriorAuthRequirement.NP_CLEARANCE]: 'NP',
  [PriorAuthRequirement.PSYCH_EVALUATION]: 'Psych',
  [PriorAuthRequirement.PCP_CLEARANCE]: 'PCP', // retired, never listed
};
const ITEMS: [PriorAuthRequirement, string][] = PRIOR_AUTH_PREP_ITEMS.map((code) => [code, LABELS[code]]);

const ROW_STYLE: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center', whiteSpace: 'nowrap' };
const ITEM_STYLE: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 };
const RING_STYLE: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  border: `1.5px solid ${QUEUE_EMPTY_COLOR}`,
  boxSizing: 'border-box',
};

function hover(label: string, item: PrepItem | undefined): string {
  if (!item || item.status === 'requested') return `${label} · open`;
  const on = item.at ? ` ${dayjs(item.at).format('MMM D')}` : '';
  return item.status === 'cancelled' ? `${label} · waived${on}` : `${label} ·${on || ' done'}`;
}

/**
 * Done reads black with a green tick, open grey with an empty ring, as the prototype draws it
 * (BAR-3098). A set opened before the questionnaire never had one: it reads grey with a dash,
 * not required (BAR-3128).
 */
export function PrepChecklist({ items }: { items: PrepItem[] }): ReactElement {
  const preQuestionnaire = isPreQuestionnaireSet(items);
  return (
    <span style={ROW_STYLE}>
      {ITEMS.map(([code, label]) => {
        if (preQuestionnaire && code === PriorAuthRequirement.QUESTIONNAIRE) {
          return (
            <Tooltip key={code} label={`${label} · not required for this set`} withArrow>
              <span style={{ ...ITEM_STYLE, color: QUEUE_EMPTY_COLOR }} data-done="n/a">
                <span style={{ fontSize: 11 }}>–</span>
                {label}
              </span>
            </Tooltip>
          );
        }
        const item = items.find((candidate) => candidate.code === code);
        const done = item !== undefined && item.status !== 'requested';
        return (
          <Tooltip key={code} label={hover(label, item)} withArrow>
            <span style={{ ...ITEM_STYLE, color: done ? undefined : QUEUE_EMPTY_COLOR }} data-done={done}>
              {done ? (
                <span style={{ color: QUEUE_DONE_TICK_COLOR, fontSize: 11 }}>✓</span>
              ) : (
                <span style={RING_STYLE} />
              )}
              {label}
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}
